import { CultivationGrammarResponse, NounGrammarResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/cultivation/cultivation-grammar.response';
import { appLocale } from '../pipes/vw-date.pipe';

/** Le nom pris au singulier (« ton ail ») ou au pluriel (« tes fèves »). */
export type NounNumber = 'singular' | 'plural';
/** Sujet (« Tes poireaux tiennent ») ou complément (« Couvre tes tomates ») : l'allemand change d'article. */
export type NounRole = 'subject' | 'object';
/** À qui sont les plantes : à la personne tutoyée (« tes fèves »), ou à elle qui parle (« je sème mes fèves »). */
export type NounOwner = 'you' | 'me';

export interface NounOptions {
  number?: NounNumber;
  role?: NounRole;
  owner?: NounOwner;
  /** `none` : le nom seul, sans possessif (« poireaux »). */
  determiner?: 'possessive' | 'none';
}

/** Ce que le reste de la phrase accorde : le nombre et le genre du groupe nommé. */
export interface NounForm {
  plural: boolean;
  /** m ou f (fr, it, es) ; m, f ou n (de) ; c ou n (nl) ; n (en). */
  gender: NounGrammarResponse['gender'];
}

/** Le nom d'une culture pour une langue, s'il a été relu. */
export function nounFor(grammar: CultivationGrammarResponse | null | undefined, lang: string): NounGrammarResponse | null {
  const code = (lang || 'fr').toLowerCase() as keyof CultivationGrammarResponse;
  return grammar?.[code] ?? null;
}

/** Un indénombrable reste au singulier (« ta mâche »). */
function numberOf(noun: NounGrammarResponse, wanted: NounNumber): NounNumber {
  return noun.mass ? 'singular' : wanted;
}

/** Les possessifs par langue, pour « tu » et pour « je ». */
const POSSESSIVES: Record<NounOwner, Record<string, { plural: string; f: string; m: string; mObject?: string; elided?: string; pluralF?: string }>> = {
  you: {
    // « ton ail », « ton herbe » : devant une voyelle, ta devient ton.
    fr: { plural: 'tes', f: 'ta', m: 'ton', elided: 'ton' },
    en: { plural: 'your', f: 'your', m: 'your' },
    nl: { plural: 'je', f: 'je', m: 'je' },
    de: { plural: 'deine', f: 'deine', m: 'dein', mObject: 'deinen' },
    // L'italien garde l'article devant le possessif : « le tue fave », « il tuo aglio ».
    it: { plural: 'i tuoi', pluralF: 'le tue', f: 'la tua', m: 'il tuo' },
    es: { plural: 'tus', f: 'tu', m: 'tu' },
  },
  me: {
    fr: { plural: 'mes', f: 'ma', m: 'mon', elided: 'mon' },
    en: { plural: 'my', f: 'my', m: 'my' },
    nl: { plural: 'mijn', f: 'mijn', m: 'mijn' },
    de: { plural: 'meine', f: 'meine', m: 'mein', mObject: 'meinen' },
    it: { plural: 'i miei', pluralF: 'le mie', f: 'la mia', m: 'il mio' },
    es: { plural: 'mis', f: 'mi', m: 'mi' },
  },
};

/** Le possessif devant le nom, par langue. */
function possessive(lang: string, noun: NounGrammarResponse, number: NounNumber, role: NounRole, owner: NounOwner): string {
  const forms = POSSESSIVES[owner][lang];
  if (!forms) return '';
  if (number === 'plural') return noun.gender === 'f' ? (forms.pluralF ?? forms.plural) : forms.plural;
  if (noun.gender === 'f') return noun.elision && forms.elided ? forms.elided : forms.f;
  // L'allemand : « deinen Lauch » en complément, « dein Lauch » en sujet ; le neutre ne change pas.
  return noun.gender === 'm' && role === 'object' && forms.mObject ? forms.mObject : forms.m;
}

/**
 * « tes pommes de terre », « ta mâche », « ton ail », « deine Kartoffeln »,
 * « deinen Lauch », « le tue fave », « tus habas » : le nom d'une culture dans
 * une phrase, avec le possessif et le nombre de la langue. `null` si le nom
 * n'a pas été relu pour cette langue : la phrase prend alors sa forme sans
 * accord.
 */
export function nounPhrase(
  grammar: CultivationGrammarResponse | null | undefined,
  lang: string,
  options: NounOptions = {},
): string | null {
  const language = (lang || 'fr').toLowerCase();
  const noun = nounFor(grammar, language);
  if (!noun) return null;
  const number = numberOf(noun, options.number ?? 'plural');
  const word = number === 'plural' ? noun.plural : noun.singular;
  if (options.determiner === 'none') return word;
  return `${possessive(language, noun, number, options.role ?? 'object', options.owner ?? 'you')} ${word}`;
}

/**
 * Plusieurs cultures dans une phrase, chacune avec son possessif :
 * « tes tomates et tes courgettes ». `null` dès qu'un nom manque.
 */
export function nounList(
  grammars: (CultivationGrammarResponse | null | undefined)[],
  lang: string,
  options: NounOptions = {},
): string | null {
  const parts = grammars.map((g) => nounPhrase(g, lang, options));
  if (!parts.length || parts.some((p) => p === null)) return null;
  const unique = [...new Set(parts as string[])];
  return new Intl.ListFormat(appLocale(lang), { style: 'long', type: 'conjunction' }).format(unique);
}

/**
 * Le nombre et le genre que la phrase accorde : un pluriel dès deux cultures
 * distinctes (ou un nom au pluriel), le masculin l'emporte en français,
 * italien et espagnol, comme le veut la grammaire. Deux pieds de mâche restent
 * une culture : « ta mâche », au singulier, comme dans `nounList`.
 */
export function nounForm(
  grammars: (CultivationGrammarResponse | null | undefined)[],
  lang: string,
  number: NounNumber = 'plural',
): NounForm | null {
  const found = grammars.map((g) => nounFor(g, lang));
  if (!found.length || found.some((n) => !n)) return null;
  const nouns = [...new Map(found.map((n) => [`${n!.singular}|${n!.plural}|${n!.gender}`, n!])).values()];
  const plural = nouns.length > 1 || nouns.some((n) => numberOf(n, number) === 'plural');
  const genders = nouns.map((n) => n.gender);
  const gender = genders.every((g) => g === genders[0]) ? genders[0] : genders.includes('m') ? 'm' : genders[0];
  return { plural, gender };
}

/**
 * La clé de traduction accordée au groupe nommé : `cle_one_f`, `cle_one`
 * (singulier), puis `cle_f` (genre), la première qui existe ; sans accord
 * connu, la clé telle quelle. « Elles étaient déjà couvertes. »
 */
export function formKey(key: string, form: NounForm | null | undefined, has: (key: string) => boolean): string {
  if (!form) return key;
  const variants = [...(!form.plural ? [`${key}_one_${form.gender}`, `${key}_one`] : []), `${key}_${form.gender}`];
  return variants.find(has) ?? key;
}
